import { useState, useRef, useEffect } from "react";
import { cn } from "../lib/utils";
import { Check } from "lucide-react";

function useOutsideClick(ref, callback) {
  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, callback]);
}

export default function SimpleSelect({
  label,
  placeholder,
  options = [],
  optionsIcons = [],
  icon,
  name,
  value,
  onChange,
  state = "default",
  helperText = false,
  additionalInfo = false,
  searchable = false,
}) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useOutsideClick(dropdownRef, () => {
    if (open) setOpen(false);
  });

  const stateStyles = {
    default: "border-[#CECBD7]",
    error: "border-red-500",
    success: "border-green-500",
    disabled: "bg-muted text-muted-foreground cursor-not-allowed opacity-70",
  };

  const filteredOptions = options.filter((opt) =>
    opt.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="w-full space-y-1" ref={dropdownRef}>
      {label && (
        <p className="block text-[14px] text-[#6D6584] mb-1">{label}</p>
      )}
      <div className="relative">
        <button
          type="button"
          disabled={state === "disabled"}
          onClick={() => setOpen(!open)}
          className={cn(
            "flex items-center justify-between w-full px-3 pt-[15px] pb-[15px] text-left bg-white border rounded-[16px]",
            stateStyles[state]
          )}
        >
          <span className={value ? "text-[#423E51]" : "text-[#9D96AF]"}>
            {value || placeholder}
          </span>
          {icon && <span className="ml-2">{icon}</span>}
        </button>

        {open && (
          <div className="absolute top-full left-0 mt-1 w-full bg-white rounded-md shadow-lg z-20">
            {searchable && (
              <div className="p-2 border-b">
                <div className="relative">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 1110.5 3a7.5 7.5 0 016.15 13.65z"
                    />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm rounded-full border border-gray-300 outline-none "
                  />
                </div>
              </div>
            )}

            <ul className="max-h-48 overflow-y-auto">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((opt) => {
                  const originalIndex = options.indexOf(opt);
                  return (
                    <li
                      key={originalIndex}
                      onClick={() => {
                        onChange?.({ target: { name, value: opt } });
                        setOpen(false);
                        setSearchTerm("");
                      }}
                      className={`group flex text-[16px] font-semibold items-center gap-5 px-3 py-2 cursor-pointer hover:bg-gray-50 ${
                        value === opt
                          ? "text-[#423E51] font-medium"
                          : "text-[#9D96AF]"
                      }`}
                    >
                      {optionsIcons[originalIndex] && (
                        <img
                          src={optionsIcons[originalIndex]}
                          alt={opt}
                          className="w-5 h-5 rounded-full object-cover"
                        />
                      )}
                      <span className="flex-1">{opt}</span>
                      {value === opt && (
                        <span className="text-[#EB3366]">
                          <Check />
                        </span>
                      )}
                    </li>
                  );
                })
              ) : (
                <li className="px-3 py-2 text-sm text-gray-400">No results</li>
              )}
            </ul>
          </div>
        )}
      </div>

      {helperText && (
        <p
          className={cn(
            "text-xs mt-1",
            state === "error" ? "text-red-500" : "text-muted-foreground"
          )}
        >
          {helperText}
        </p>
      )}
      {additionalInfo && (
        <p className="text-xs text-muted-foreground">{additionalInfo}</p>
      )}
    </div>
  );
}
